import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    User,
    Mail,
    Phone,
    CheckCircle,
    AlertCircle,
    Loader2,
    Info,
    School,
    MapPin
} from 'lucide-react';

const CustomSchoolEnrollment = () => {
    const { schoolName } = useParams();
    const [schoolInfo, setSchoolInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        parentsEmail: '',
        motherFirstName: '',
        motherLastName: '',
        motherEmail: '',
        motherPhone: '',
        fatherFirstName: '',
        fatherLastName: '',
        fatherEmail: '',
        fatherPhone: '',
        latitude: null,
        longitude: null
    });

    const [submitLoading, setSubmitLoading] = useState(false);
    const [geolocationStatus, setGeolocationStatus] = useState('pending');
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState('');

    // Fetch school information
    useEffect(() => {
        const fetchSchoolInfo = async () => {
            try {
                const response = await fetch(`/api/enrollments/school/${schoolName}`);
                if (response.ok) {
                    const data = await response.json();
                    setSchoolInfo(data);
                } else {
                    setSubmitStatus('error');
                }
            } catch (error) {
                console.error('Error fetching school info:', error);
                setSubmitStatus('error');
            } finally {
                setLoading(false);
            }
        };

        fetchSchoolInfo();
    }, [schoolName]);

    // Geolocation effect
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFormData(prev => ({
                        ...prev,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    }));
                    setGeolocationStatus('approved');
                },
                (error) => {
                    setGeolocationStatus('denied');
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 600000
                }
            );
        } else {
            setGeolocationStatus('error');
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.parentsEmail.trim()) newErrors.parentsEmail = 'Parent email is required';
        if (!formData.motherFirstName.trim()) newErrors.motherFirstName = 'Mother first name is required';
        if (!formData.motherLastName.trim()) newErrors.motherLastName = 'Mother last name is required';
        if (!formData.motherEmail.trim()) newErrors.motherEmail = 'Mother email is required';
        if (!formData.motherPhone.trim()) newErrors.motherPhone = 'Mother phone is required';
        if (!formData.fatherFirstName.trim()) newErrors.fatherFirstName = 'Father first name is required';
        if (!formData.fatherLastName.trim()) newErrors.fatherLastName = 'Father last name is required';
        if (!formData.fatherEmail.trim()) newErrors.fatherEmail = 'Father email is required';
        if (!formData.fatherPhone.trim()) newErrors.fatherPhone = 'Father phone is required';

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.parentsEmail && !emailRegex.test(formData.parentsEmail)) {
            newErrors.parentsEmail = 'Please enter a valid email address';
        }
        if (formData.motherEmail && !emailRegex.test(formData.motherEmail)) {
            newErrors.motherEmail = 'Please enter a valid email address';
        }
        if (formData.fatherEmail && !emailRegex.test(formData.fatherEmail)) {
            newErrors.fatherEmail = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm() || !schoolInfo) {
            return;
        }

        setSubmitLoading(true);
        setSubmitStatus('');

        try {
            const submissionData = {
                ...formData,
                schoolId: schoolInfo.schoolId || null
            };

            const response = await fetch('/api/students', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submissionData)
            });

            if (response.ok) {
                setSubmitStatus('success');
                // Reset form
                setFormData({
                    firstName: '',
                    lastName: '',
                    parentsEmail: '',
                    motherFirstName: '',
                    motherLastName: '',
                    motherEmail: '',
                    motherPhone: '',
                    fatherFirstName: '',
                    fatherLastName: '',
                    fatherEmail: '',
                    fatherPhone: '',
                    latitude: formData.latitude,
                    longitude: formData.longitude
                });
            } else {
                const errorData = await response.json();
                setSubmitStatus('error');
                if (errorData.details) {
                    const fieldErrors = {};
                    errorData.details.forEach(error => {
                        // Map error messages to form fields
                        if (error.includes('First name')) fieldErrors.firstName = error;
                        else if (error.includes('Last name')) fieldErrors.lastName = error;
                        else if (error.includes('parent email')) fieldErrors.parentsEmail = error;
                        else if (error.includes('Mother')) {
                            if (error.includes('email')) fieldErrors.motherEmail = error;
                            else if (error.includes('phone')) fieldErrors.motherPhone = error;
                            else if (error.includes('first name')) fieldErrors.motherFirstName = error;
                            else if (error.includes('last name')) fieldErrors.motherLastName = error;
                        }
                        else if (error.includes('Father')) {
                            if (error.includes('email')) fieldErrors.fatherEmail = error;
                            else if (error.includes('phone')) fieldErrors.fatherPhone = error;
                            else if (error.includes('first name')) fieldErrors.fatherFirstName = error;
                            else if (error.includes('last name')) fieldErrors.fatherLastName = error;
                        }
                    });
                    setErrors(fieldErrors);
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setSubmitLoading(false);
        }
    };

    const Tooltip = ({ children, text }) => (
        <div className="relative group">
            {children}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                {text}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 size={48} className="text-primary-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading enrollment page...</p>
                </div>
            </div>
        );
    }

    if (!schoolInfo) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-8">
                    <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">School Not Found</h1>
                    <p className="text-gray-600 mb-6">
                        The enrollment page for "{schoolName}" could not be found. Please check the URL or contact the school for the correct link.
                    </p>
                    <a href="/" className="btn-primary">
                        Return to Homepage
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl mx-auto"
                >
                    {/* School Logo and Title */}
                    <div className="text-center mb-12">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                            className="mb-6"
                        >
                            {schoolInfo.logoUrl ? (
                                <img
                                    src={schoolInfo.logoUrl}
                                    alt={`${schoolInfo.name} Logo`}
                                    className="w-32 h-32 mx-auto mb-4 object-contain"
                                />
                            ) : (
                                <div className="w-32 h-32 mx-auto mb-4 bg-primary-100 rounded-full flex items-center justify-center">
                                    <School size={48} className="text-primary-600" />
                                </div>
                            )}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                                {schoolInfo.name}
                            </h1>
                            <p className="text-lg text-gray-600 mb-4">
                                {schoolInfo.district}, {schoolInfo.region}
                            </p>
                            <h2 className="text-2xl md:text-3xl font-bold text-primary-600 mb-4">
                                Student Enrollment
                            </h2>
                            <p className="text-lg text-gray-600 max-w-xl mx-auto">
                                Join our early sign-up list! You're not committing to anything - just securing your spot
                                for when we launch with a 1,200 CFA discount.
                            </p>
                        </motion.div>
                    </div>

                    {/* Form Container with Animated Border */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500 opacity-75 animate-pulse"></div>
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500 opacity-75 animate-spin" style={{ animationDuration: '3s' }}></div>

                        <div className="relative bg-white rounded-2xl p-8 m-1">
                            {/* Success Message */}
                            {submitStatus === 'success' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
                                >
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle size={20} className="text-green-600" />
                                        <span className="text-green-800 font-medium">
                                            Enrollment submitted successfully! You'll receive a confirmation email soon.
                                        </span>
                                    </div>
                                </motion.div>
                            )}

                            {/* Error Message */}
                            {submitStatus === 'error' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
                                >
                                    <div className="flex items-center space-x-2">
                                        <AlertCircle size={20} className="text-red-600" />
                                        <span className="text-red-800 font-medium">
                                            There was an error submitting your enrollment. Please check your information and try again.
                                        </span>
                                    </div>
                                </motion.div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Student Information */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <User size={20} className="text-primary-600" />
                                        <span>Student Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Tooltip text="Enter the student's first name as it appears on official documents">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Student First Name *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter first name"
                                            />
                                            {errors.firstName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
                                            )}
                                        </div>

                                        <div>
                                            <Tooltip text="Enter the student's last name as it appears on official documents">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Student Last Name *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter last name"
                                            />
                                            {errors.lastName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <Tooltip text="Primary email for important communications and updates">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Parents Email *
                                                <Info size={14} className="inline ml-1 text-gray-400" />
                                            </label>
                                        </Tooltip>
                                        <input
                                            type="email"
                                            name="parentsEmail"
                                            value={formData.parentsEmail}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.parentsEmail ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="parent@example.com"
                                        />
                                        {errors.parentsEmail && (
                                            <p className="text-red-600 text-sm mt-1">{errors.parentsEmail}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Mother Information */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <User size={20} className="text-accent-600" />
                                        <span>Mother Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                First Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="motherFirstName"
                                                value={formData.motherFirstName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherFirstName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter mother's first name"
                                            />
                                            {errors.motherFirstName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherFirstName}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Last Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="motherLastName"
                                                value={formData.motherLastName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherLastName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter mother's last name"
                                            />
                                            {errors.motherLastName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherLastName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                name="motherEmail"
                                                value={formData.motherEmail}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherEmail ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="mother@example.com"
                                            />
                                            {errors.motherEmail && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherEmail}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Phone *
                                            </label>
                                            <input
                                                type="tel"
                                                name="motherPhone"
                                                value={formData.motherPhone}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherPhone ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="+220 XXX XXXX"
                                            />
                                            {errors.motherPhone && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherPhone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Father Information */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <User size={20} className="text-secondary-600" />
                                        <span>Father Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                First Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="fatherFirstName"
                                                value={formData.fatherFirstName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherFirstName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter father's first name"
                                            />
                                            {errors.fatherFirstName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherFirstName}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Last Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="fatherLastName"
                                                value={formData.fatherLastName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherLastName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter father's last name"
                                            />
                                            {errors.fatherLastName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherLastName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                name="fatherEmail"
                                                value={formData.fatherEmail}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherEmail ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="father@example.com"
                                            />
                                            {errors.fatherEmail && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherEmail}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Phone *
                                            </label>
                                            <input
                                                type="tel"
                                                name="fatherPhone"
                                                value={formData.fatherPhone}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherPhone ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="+220 XXX XXXX"
                                            />
                                            {errors.fatherPhone && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherPhone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-6">
                                    <button
                                        type="submit"
                                        disabled={submitLoading}
                                        className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                    >
                                        {submitLoading ? (
                                            <>
                                                <Loader2 size={20} className="animate-spin" />
                                                <span>Submitting Enrollment...</span>
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={20} />
                                                <span>Submit Student Enrollment</span>
                                            </>
                                        )}
                                    </button>

                                    <p className="text-center text-sm text-gray-600 mt-4">
                                        By submitting, you're joining our early sign-up list for {schoolInfo.name} with a guaranteed spot and 1,200 CFA discount.
                                    </p>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default CustomSchoolEnrollment;